import { lazy, ComponentType } from 'react';

const RELOAD_KEY = 'chunk_reload_at';
const RELOAD_WINDOW = 30000;

const CHUNK_ERROR =
  /Importing a module script failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Unable to preload CSS/i;

export function isChunkLoadError(error: unknown) {
  return CHUNK_ERROR.test((error as Error)?.message ?? '');
}

// A deploy replaces every hashed chunk, so a tab opened before it can no longer resolve
// the chunks its index.html points at. Reloading picks up the new manifest, but only once —
// if the chunk is genuinely missing we must not spin in a reload loop.
export function reloadOnce() {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_KEY) ?? 0);
    if (Date.now() - last < RELOAD_WINDOW) {
      return false;
    }
    window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable (private mode, blocked cookies) — reload without the guard
  }
  window.location.reload();
  return true;
}

export default function lazyWithRetry<T extends ComponentType<any>>(factory: () => Promise<{ default: T }>) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (error) {
      if (isChunkLoadError(error) && reloadOnce()) {
        // Hold Suspense open until the reload lands instead of flashing the error page
        return new Promise<{ default: T }>(() => {});
      }
      throw error;
    }
  });
}
